/**
 * viewportScreenshot.js — Enterprise Screenshot & Export Engine
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v3.0 — ULTIMATE SCREENSHOT SUITE]:
 *  - readPixels: PACK_ALIGNMENT = 1 (защита от некратных ширин)
 *  - batchExportSlices: setSliceIndex(0) вместо цикла scroll(-1)
 *  - ZIP экспорт батча (через динамический импорт JSZip)
 *  - [12.0.52] Animated GIF — перенесён в ui/exportModule.exportAsGif
 *  - WebP поддержка (формат 'webp' в screenshotViewport)
 *  - Overlay customisation (показывать/скрывать orientation, patient, timestamp)
 *  - Аннотации на скриншоте (наложение текущих измерений)
 *  - Скриншот в буфер с рамкой (RadiAnt-style)
 *  - Сравнение до/после (два скриншота рядом с разметкой)
 *  - Screen recording (MediaRecorder из canvas)
 *  - Bulk export metadata (CSV с координатами и HU)
 *
 * @module viewportScreenshot
 */

import state, { getRenderingEngine, getViewportIds, OrientationAxis, cornerstone, cornerstoneTools } from './viewportState.js';
import { events } from './events.js';
import { EVENTS } from './eventBus.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
import { КОРОТКО, СТРОКА } from './disclaimer.js';   // [10.19.9] оговорка уезжает вместе с числами
import { собратьSC, ПРИСТАВКА_UID } from './scDicom.js';   // [12.0.57] файл SC собирается чистым ядром
// [12.0.52] Импорт адреса gif.worker убран вместе с exportAnimatedGIF (GIF — в ui/exportModule.js).

// ══════════════════════════════════════════════════════════════
//  CORE SCREENSHOT
// ══════════════════════════════════════════════════════════════

export function screenshotViewport(viewportId, opts = {}) {
  const format = opts.format || 'png';
  const quality = opts.quality || 0.95;
  const drawOverlay = opts.overlay !== false;
  const overlayOpts = opts.overlayOptions || {};

  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return null;
  const canvas = vp.canvas || vp.element?.querySelector('canvas');
  if (!canvas) return null;

  const offscreen = document.createElement('canvas');
  offscreen.width = canvas.width;
  offscreen.height = canvas.height;
  const ctx = offscreen.getContext('2d');

  try {
    ctx.drawImage(canvas, 0, 0);
  } catch {
    // WebGL readPixels fallback
    try {
      const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
      if (!gl) return null;

      gl.pixelStorei(gl.PACK_ALIGNMENT, 1); // фикс для некратных ширин

      const w = canvas.width, h = canvas.height;
      const pixels = new Uint8Array(w * h * 4);
      gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

      const imgData = ctx.createImageData(w, h);
      for (let y = 0; y < h; y++) {
        const src = (h - y - 1) * w * 4;
        imgData.data.set(pixels.subarray(src, src + w * 4), y * w * 4);
      }
      ctx.putImageData(imgData, 0, 0);
    } catch (e2) {
      log.error('[Screenshot] readPixels failed:', e2.message);
      return null;
    }
  }

  if (drawOverlay) _drawOverlay(ctx, offscreen.width, offscreen.height, viewportId, opts.patientInfo, overlayOpts);
  if (opts.watermark) _drawWatermark(ctx, offscreen.width, offscreen.height, opts.watermark);

  // Поддержка WebP
  const mimeType = format === 'webp' ? 'image/webp' : `image/${format}`;
  return offscreen.toDataURL(mimeType, quality);
}

export function downloadScreenshot(viewportId, filename, opts = {}) {
  const dataUrl = screenshotViewport(viewportId, opts);
  if (!dataUrl) { showToast('❌ Не удалось сделать скриншот', 'error', 3000); return; }
  _downloadDataUrl(dataUrl, filename || `viewport-${viewportId}-${Date.now()}.png`);
  showToast('📸 Скриншот сохранён', 'success', 2000);
}

export async function copyScreenshotToClipboard(viewportId, opts = {}) {
  try {
    const re = getRenderingEngine();
    const vp = re?.getViewport(viewportId || getViewportIds()[0]);
    if (!vp) return false;
    const canvas = vp.canvas || vp.element?.querySelector('canvas');
    if (!canvas) return false;

    const blob = await new Promise((resolve, reject) => {
      if (canvas.toBlob) {
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png');
      } else {
        // Fallback для Safari
        const dataUrl = canvas.toDataURL('image/png');
        fetch(dataUrl).then(r => r.blob()).then(resolve).catch(reject);
      }
    });
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    showToast('📋 Скопировано в буфер', 'success', 1500);
    return true;
  } catch (e) {
    log.warn('[Screenshot] Clipboard:', e.message);
    showToast('❌ Не удалось скопировать', 'error', 2000);
    return false;
  }
}

// ══════════════════════════════════════════════════════════════
//  SCREENSHOT С РАМКОЙ (RadiAnt-style)
// ══════════════════════════════════════════════════════════════

/**
 * Скриншот с белой рамкой и подписью как в RadiAnt.
 * @param {string} viewportId
 * @param {Object} [opts]
 * @param {string} [opts.title] — заголовок под скриншотом
 * @param {Object} [opts.patientInfo] — информация о пациенте
 * @param {number} [opts.borderPx=20] — ширина рамки
 */
export function screenshotWithFrame(viewportId, opts = {}) {
  const dataUrl = screenshotViewport(viewportId, { ...opts, overlay: false });
  if (!dataUrl) return null;

  const img = new Image();
  return new Promise((resolve) => {
    img.onload = () => {
      const border = opts.borderPx || 20;
      const titleHeight = opts.title ? 40 : 0;
      // [10.19.9] Оговорка печатается на самом снимке. Футер программы в файл не
      // уезжает, а числа на снимке — уезжают: через месяц такой PNG выглядит как
      // документ. Отказаться от неё можно только явно (opts.disclaimer === false),
      // чтобы это было видно в коде вызывающего, а не получалось само собой.
      const оговорка = opts.disclaimer === false ? '' : (opts.disclaimerText || СТРОКА);
      const шрифтОговорки = 11;
      const оговоркаH = оговорка ? шрифтОговорки + 10 : 0;
      const canvas = document.createElement('canvas');
      canvas.width = img.width + border * 2;
      canvas.height = img.height + border * 2 + titleHeight + оговоркаH;
      const ctx = canvas.getContext('2d');

      // Белая рамка
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Изображение
      ctx.drawImage(img, border, border);

      // Заголовок
      if (opts.title) {
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 16px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(opts.title, canvas.width / 2, img.height + border + 28);
      }

      // Оговорка — мелким, но читаемым, и с переносом, если не влезает.
      if (оговорка) {
        ctx.fillStyle = '#8a6d00';
        ctx.font = `${шрифтОговорки}px sans-serif`;
        ctx.textAlign = 'center';
        const y = canvas.height - 8;
        const макс = canvas.width - 12;
        if (ctx.measureText(оговорка).width <= макс) {
          ctx.fillText(оговорка, canvas.width / 2, y);
        } else {
          // Не влезло — оставляем короткую форму, обрезать смысл нельзя.
          ctx.font = `bold ${шрифтОговорки}px sans-serif`;
          ctx.fillText(КОРОТКО, canvas.width / 2, y);
        }
      }

      resolve(canvas.toDataURL('image/png', 0.95));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

// ══════════════════════════════════════════════════════════════
//  COLLAGE
// ══════════════════════════════════════════════════════════════

export async function screenshotAll(opts = {}) {
  try {
    const ids = getViewportIds();
    if (!ids.length) return null;
    const shots = ids
      .map(id => ({ id, dataUrl: screenshotViewport(id, { ...opts, overlay: true }) }))
      .filter(s => s.dataUrl);
    if (!shots.length) return null;

    const cols = shots.length <= 2 ? shots.length : Math.ceil(Math.sqrt(shots.length));
    const images = await Promise.all(shots.map(s =>
      new Promise(r => { const img = new Image(); img.onload = () => r(img); img.onerror = () => r(null); img.src = s.dataUrl; })
    ));
    const valid = images.filter(Boolean);
    if (!valid.length) return null;

    const cellW = valid[0].width, cellH = valid[0].height;
    const rows = Math.ceil(valid.length / cols);
    const collage = document.createElement('canvas');
    collage.width = cellW * cols;
    collage.height = cellH * rows;
    const ctx = collage.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, collage.width, collage.height);
    valid.forEach((im, i) => ctx.drawImage(im, (i % cols) * cellW, Math.floor(i / cols) * cellH));

    const format = opts.format === 'webp' ? 'image/webp' : `image/${opts.format || 'png'}`;
    return collage.toDataURL(format, opts.quality || 0.95);
  } catch (e) { log.warn(`[viewportScreenshot] screenshotAll failed:`, e.message); return null; }
}

// ══════════════════════════════════════════════════════════════
//  COMPARISON STRIP
// ══════════════════════════════════════════════════════════════

export async function createComparisonStrip(viewportId, numFrames = 6, opts = {}) {
  try { (await import('../ui/exportModule.js')).отметитьЭкспорт('лента'); } catch (e) { /* история — не повод */ }
  try {
    const re = getRenderingEngine();
    const vp = re?.getViewport(viewportId);
    if (!vp) return null;

    const isStack = typeof vp.setImageIdIndex === 'function';
    let total = isStack ? (vp.getImageIds?.()?.length || 0) : (vp.getNumberOfSlices?.() || 100);
    if (!total) return null;

    const originalIdx = isStack ? (vp.getCurrentImageIdIndex?.() || 0) : (vp.getSliceIndex?.() || 0);
    const step = Math.max(1, Math.floor(total / numFrames));
    const frames = [];

    if (isStack) {
      for (let i = 0; i < numFrames && i * step < total; i++) {
        const idx = i * step;
        vp.setImageIdIndex(idx);
        vp.render();
        await new Promise(r => setTimeout(r, 80));
        const shot = screenshotViewport(viewportId, { overlay: false });
        if (shot) frames.push({ dataUrl: shot, index: idx });
      }
      vp.setImageIdIndex(originalIdx);
    } else {
      // Быстрый переход к началу
      try { if (typeof vp.setSliceIndex === 'function') vp.setSliceIndex(0); } catch {}
      vp.render();
      await new Promise(r => setTimeout(r, 80));

      for (let i = 0; i < numFrames && i * step < total; i++) {
        const targetSlice = i * step;
        try {
          if (typeof vp.setSliceIndex === 'function') {
            vp.setSliceIndex(targetSlice);
          } else {
            vp.scroll(targetSlice - (vp.getSliceIndex?.() || 0));
          }
        } catch {}
        vp.render();
        await new Promise(r => setTimeout(r, 80));
        const shot = screenshotViewport(viewportId, { overlay: false });
        if (shot) frames.push({ dataUrl: shot, index: targetSlice });
      }
      try { if (typeof vp.setSliceIndex === 'function') vp.setSliceIndex(originalIdx); } catch {}
    }
    vp.render();

    if (!frames.length) return null;

    const images = await Promise.all(frames.map(f =>
      new Promise(r => { const img = new Image(); img.onload = () => r(img); img.onerror = () => r(null); img.src = f.dataUrl; })
    ));
    const valid = images.filter(Boolean);
    if (!valid.length) return null;

    const frameW = opts.width ? Math.round(opts.width / valid.length) : valid[0].width;
    const frameH = Math.round(frameW * valid[0].height / valid[0].width);
    const strip = document.createElement('canvas');
    strip.width = frameW * valid.length;
    strip.height = frameH + 24;
    const ctx = strip.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, strip.width, strip.height);

    valid.forEach((img, i) => {
      ctx.drawImage(img, i * frameW, 0, frameW, frameH);
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.font = '11px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(`#${frames[i].index + 1}/${total}`, i * frameW + frameW / 2, frameH + 16);
      if (i > 0) {
        ctx.strokeStyle = 'rgba(255,255,255,0.3)';
        ctx.beginPath();
        ctx.moveTo(i * frameW, 0);
        ctx.lineTo(i * frameW, frameH);
        ctx.stroke();
      }
    });

    if (opts.watermark) _drawWatermark(ctx, strip.width, strip.height, opts.watermark);

    const format = opts.format === 'webp' ? 'image/webp' : 'image/png';
    log.debug(`[Screenshot] Comparison strip: ${valid.length} frames`);
    return strip.toDataURL(format, 0.95);
  } catch (e) { log.warn(`[viewportScreenshot] createComparisonStrip failed:`, e.message); return null; }
}

// ══════════════════════════════════════════════════════════════
//  СРАВНЕНИЕ ДО/ПОСЛЕ
// ══════════════════════════════════════════════════════════════

/**
 * Создать изображение со сравнением двух скриншотов (до/после).
 * @param {string} viewportId
 * @param {Object} beforeDataUrl — data URL скриншота ДО
 * @param {Object} [opts]
 * @returns {Promise<string|null>}
 */
export async function createBeforeAfterComparison(viewportId, beforeDataUrl, opts = {}) {
  const afterDataUrl = screenshotViewport(viewportId, { overlay: false });
  if (!beforeDataUrl || !afterDataUrl) return null;

  const [beforeImg, afterImg] = await Promise.all([beforeDataUrl, afterDataUrl].map(url =>
    new Promise(r => { const img = new Image(); img.onload = () => r(img); img.onerror = () => r(null); img.src = url; })
  ));
  if (!beforeImg || !afterImg) return null;

  const maxW = Math.max(beforeImg.width, afterImg.width);
  const maxH = Math.max(beforeImg.height, afterImg.height);
  const gap = 4;
  const labelH = 24;

  const canvas = document.createElement('canvas');
  canvas.width = maxW * 2 + gap;
  canvas.height = maxH + labelH;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.drawImage(beforeImg, 0, labelH, maxW, maxH);
  ctx.drawImage(afterImg, maxW + gap, labelH, maxW, maxH);

  // Разделительная линия
  ctx.strokeStyle = '#ff4444';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(maxW + gap / 2, labelH);
  ctx.lineTo(maxW + gap / 2, canvas.height);
  ctx.stroke();

  // Подписи
  ctx.fillStyle = '#ff4444';
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(opts.beforeLabel || 'ДО', maxW / 2, 18);
  ctx.fillStyle = '#44ff44';
  ctx.fillText(opts.afterLabel || 'ПОСЛЕ', maxW + gap + maxW / 2, 18);

  // Процент изменения если задан
  if (opts.percentChange != null) {
    const changeText = `${opts.percentChange > 0 ? '+' : ''}${opts.percentChange}%`;
    const changeColor = Math.abs(opts.percentChange) > 20 ? '#ff4444' : Math.abs(opts.percentChange) > 10 ? '#ffaa00' : '#44ff44';
    ctx.fillStyle = changeColor;
    ctx.font = 'bold 24px sans-serif';
    ctx.fillText(changeText, canvas.width / 2, canvas.height - 10);
  }

  return canvas.toDataURL('image/png', 0.95);
}

// ══════════════════════════════════════════════════════════════
//  BATCH EXPORT + ZIP
// ══════════════════════════════════════════════════════════════

export async function batchExportSlices(viewportId, opts = {}) {
  try { (await import('../ui/exportModule.js')).отметитьЭкспорт('пакет срезов'); } catch (e) { /* история — не повод */ }
  try {
    const re = getRenderingEngine();
    const vp = re?.getViewport(viewportId);
    if (!vp) return [];

    const isStack = typeof vp.setImageIdIndex === 'function';
    let total = isStack ? (vp.getImageIds?.()?.length || 0) : (vp.getNumberOfSlices?.() || 0);
    if (!total) { showToast('⚠️ Нет срезов для экспорта', 'warning', 2000); return []; }

    const step = opts.step || 1;
    const results = [];

    if (isStack) {
      const originalIdx = vp.getCurrentImageIdIndex?.() || 0;
      for (let i = 0; i < total; i += step) {
        vp.setImageIdIndex(i);
        vp.render();
        await new Promise(r => setTimeout(r, 80));
        const shot = screenshotViewport(viewportId, { overlay: false, format: opts.format });
        if (shot) results.push({ index: i, dataUrl: shot });
        if (opts.onProgress) opts.onProgress(results.length, Math.ceil(total / step));
      }
      vp.setImageIdIndex(originalIdx);
    } else {
      const originalIdx = vp.getSliceIndex?.() || 0;
      try { if (typeof vp.setSliceIndex === 'function') vp.setSliceIndex(0); } catch {}
      vp.render();
      await new Promise(r => setTimeout(r, 80));

      for (let i = 0; i < total; i += step) {
        try {
          if (typeof vp.setSliceIndex === 'function') vp.setSliceIndex(i);
          else vp.scroll(i - (vp.getSliceIndex?.() || 0));
        } catch {}
        vp.render();
        await new Promise(r => setTimeout(r, 80));
        const shot = screenshotViewport(viewportId, { overlay: false, format: opts.format });
        if (shot) results.push({ index: i, dataUrl: shot });
        if (opts.onProgress) opts.onProgress(results.length, Math.ceil(total / step));
      }
      try { if (typeof vp.setSliceIndex === 'function') vp.setSliceIndex(originalIdx); } catch {}
    }
    vp.render();

    log.debug(`[Screenshot] Batch: ${results.length} slices exported`);

    // Если запрошен ZIP
    if (opts.zip) {
      return await _createZipFromSlices(results, opts);
    }

    return results;
  } catch (e) { log.warn(`[viewportScreenshot] batchExportSlices failed:`, e.message); return []; }
}

async function _createZipFromSlices(slices, opts) {
  try {
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    const ext = opts.format === 'webp' ? 'webp' : 'png';

    for (const slice of slices) {
      const base64 = slice.dataUrl.split(',')[1];
      zip.file(`slice_${String(slice.index).padStart(4, '0')}.${ext}`, base64, { base64: true });
    }

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    _downloadDataUrl(url, `slices_${Date.now()}.zip`);
    URL.revokeObjectURL(url);
    showToast(`📦 ${slices.length} срезов упаковано в ZIP`, 'success', 2000);
    return slices;
  } catch (e) {
    log.warn('[Screenshot] JSZip not available:', e.message);
    return slices;
  }
}

// ══════════════════════════════════════════════════════════════
//  [12.0.52] УДАЛЕНО: exportAnimatedGIF()
// ══════════════════════════════════════════════════════════════
// Вторая дорога к GIF рядом с exportModule.exportAsGif. Звала её только
// кнопка «Экспорт GIF» с диапазоном 0…50, а в окнах MPR кадры не менялись:
// у объёмного окна нет setSliceIndex, вызов молча пропускался, и GIF
// состоял из одинаковых кадров. Кнопка теперь идёт через exportAsGif.

// ══════════════════════════════════════════════════════════════
//  SCREEN RECORDING
// ══════════════════════════════════════════════════════════════

let _screenRecorder = null;
let _screenRecordingChunks = [];

/**
 * Начать запись экрана viewport'а.
 * @param {string} viewportId
 * @param {Object} [opts]
 * @param {number} [opts.fps=30]
 * @param {string} [opts.mimeType='video/webm']
 * @returns {boolean}
 */
export function startScreenRecording(viewportId, opts = {}) {
  if (_screenRecorder) return false;

  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return false;

  const canvas = vp.canvas || vp.element?.querySelector('canvas');
  if (!canvas) return false;

  try {
    const fps = opts.fps || 30;
    const mimeType = opts.mimeType || 'video/webm';
    const stream = canvas.captureStream(fps);
    _screenRecordingChunks = [];
    _screenRecorder = new MediaRecorder(stream, { mimeType });
    _screenRecorder.ondataavailable = (e) => _screenRecordingChunks.push(e.data);
    _screenRecorder.start();
    showToast('🔴 Запись экрана...', 'info', 2000);
    return true;
  } catch (e) {
    log.warn('[Screenshot] Screen recording failed:', e.message);
    return false;
  }
}

/**
 * Остановить запись и вернуть Blob.
 * @returns {Promise<Blob|null>}
 */
export function stopScreenRecording() {
  if (!_screenRecorder) return null;

  return new Promise((resolve) => {
    _screenRecorder.onstop = () => {
      const blob = new Blob(_screenRecordingChunks, { type: _screenRecorder.mimeType });
      _screenRecorder = null;
      _screenRecordingChunks = [];
      showToast('⏹ Запись сохранена', 'success', 2000);
      resolve(blob);
    };
    _screenRecorder.stop();
  });
}

// ══════════════════════════════════════════════════════════════
//  BULK EXPORT METADATA (CSV)
// ══════════════════════════════════════════════════════════════

/**
 * Экспортировать CSV с метаданными для каждого среза.
 * @param {string} viewportId
 * @param {Object} [opts]
 * @returns {Promise<string|null>} CSV строка
 */
export async function exportSliceMetadataCSV(viewportId, opts = {}) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return null;

  const isStack = typeof vp.setImageIdIndex === 'function';
  const rows = [['SliceIndex', 'InstanceNumber', 'SliceLocation', 'ImagePositionPatient', 'WindowCenter', 'WindowWidth', 'MeanHU', 'StdHU']];

  if (isStack) {
    const ids = vp.getImageIds?.() || [];
    const originalIdx = vp.getCurrentImageIdIndex?.() || 0;

    for (let i = 0; i < ids.length; i++) {
      vp.setImageIdIndex(i);
      vp.render();
      await new Promise(r => setTimeout(r, 50));

      const img = cornerstone.cache.getImage?.(ids[i]);
      const meta = cornerstone.metaData.get('imagePlaneModule', ids[i]);
      const voi = cornerstone.metaData.get('voiLutModule', ids[i]);

      let meanHU = '', stdHU = '';
      const pd = img?.getPixelData?.();
      if (pd) {
        const step = Math.max(1, Math.floor(pd.length / 50000));
        let sum = 0, count = 0, sumSq = 0;
        for (let j = 0; j < pd.length; j += step) {
          const val = pd[j] * (img.slope || 1) + (img.intercept || 0);
          sum += val;
          sumSq += val * val;
          count++;
        }
        meanHU = (sum / count).toFixed(1);
        stdHU = Math.sqrt(sumSq / count - (sum / count) ** 2).toFixed(1);
      }

      rows.push([
        i,
        meta?.InstanceNumber || '',
        meta?.sliceLocation?.toFixed?.(2) || '',
        meta?.imagePositionPatient?.map(v => v.toFixed(2)).join(',') || '',
        voi?.windowCenter || '',
        voi?.windowWidth || '',
        meanHU,
        stdHU,
      ]);
    }

    vp.setImageIdIndex(originalIdx);
    vp.render();
  }

  return rows.map(r => r.join(',')).join('\n');
}

// ══════════════════════════════════════════════════════════════
//  WATERMARK
// ══════════════════════════════════════════════════════════════

function _drawWatermark(ctx, w, h, watermark) {
  ctx.save();
  ctx.globalAlpha = watermark.opacity || 0.15;
  ctx.font = `${watermark.fontSize || Math.round(h * 0.06)}px sans-serif`;
  ctx.fillStyle = watermark.color || '#ffffff';
  ctx.textAlign = 'center';
  ctx.translate(w / 2, h / 2);
  ctx.rotate(watermark.angle || -0.3);
  ctx.fillText(watermark.text || 'CONFIDENTIAL', 0, 0);
  ctx.restore();
}

// ══════════════════════════════════════════════════════════════
//  OVERLAY (с кастомизацией)
// ══════════════════════════════════════════════════════════════

function _drawOverlay(ctx, w, h, viewportId, patientInfo, overlayOpts = {}) {
  const showOrientation = overlayOpts.orientation !== false;
  const showPatient = overlayOpts.patient !== false;
  const showTimestamp = overlayOpts.timestamp !== false;
  const showAnnotations = overlayOpts.annotations !== false;

  ctx.font = `${Math.max(14, Math.round(h * 0.022))}px monospace`;
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.shadowColor = 'rgba(0,0,0,0.8)';
  ctx.shadowBlur = 3;

  // Orientation markers
  if (showOrientation) {
    const vpEntry = state.viewports.get(viewportId);
    if (vpEntry?.orientation !== undefined) {
      const m = _getOrientationMarkers(vpEntry.orientation);
      if (m) {
        ctx.textAlign = 'center';
        ctx.fillText(m.top, w / 2, 22);
        ctx.fillText(m.bottom, w / 2, h - 8);
        ctx.textAlign = 'left';
        ctx.fillText(m.left, 8, h / 2);
        ctx.textAlign = 'right';
        ctx.fillText(m.right, w - 8, h / 2);
      }
    }
  }

  // Patient info
  if (showPatient && patientInfo) {
    ctx.textAlign = 'left';
    ctx.shadowBlur = 2;
    const lines = [patientInfo.name, patientInfo.id && `ID: ${patientInfo.id}`, patientInfo.date, patientInfo.institution].filter(Boolean);
    const lh = Math.max(16, Math.round(h * 0.024));
    lines.forEach((l, i) => ctx.fillText(l, 10, h - 10 - (lines.length - 1 - i) * lh));
  }

  // Timestamp
  if (showTimestamp) {
    ctx.textAlign = 'right';
    ctx.font = `${Math.max(11, Math.round(h * 0.016))}px monospace`;
    ctx.fillText(new Date().toLocaleString('ru-RU'), w - 10, 20);
  }

  // Annotations overlay (текущие измерения)
  if (showAnnotations) {
    try {
      const allAnns = cornerstoneTools?.annotation?.state?.getAllAnnotations?.() || [];
      const vpAnns = allAnns.filter(a => a.metadata?.viewportId === viewportId);
      if (vpAnns.length > 0) {
        ctx.textAlign = 'right';
        ctx.fillStyle = 'rgba(255,255,100,0.9)';
        ctx.font = `${Math.max(10, Math.round(h * 0.014))}px monospace`;
        let yOff = 40;
        for (const ann of vpAnns.slice(0, 10)) {
          const label = ann.metadata?.toolName || ann.type || 'Measurement';
          ctx.fillText(label, w - 10, yOff);
          yOff += 16;
        }
      }
    } catch (e) { /* annotations unavailable */ }
  }

  ctx.shadowBlur = 0;
}

function _getOrientationMarkers(o) {
  return ({
    [OrientationAxis.AXIAL]:    { top: 'A', bottom: 'P', left: 'R', right: 'L' },
    [OrientationAxis.SAGITTAL]: { top: 'S', bottom: 'I', left: 'A', right: 'P' },
    [OrientationAxis.CORONAL]:  { top: 'S', bottom: 'I', left: 'R', right: 'L' },
  })[o] || null;
}

function _downloadDataUrl(dataUrl, filename) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ══════════════════════════════════════════════════════════════
//  DICOM SECONDARY CAPTURE
// ══════════════════════════════════════════════════════════════

/**
 * [12.0.57] Сборка байтов — core/scDicom.js (теги по возрастанию, UTF-8,
 * чётные длины, модальность OT). Пациент и исследование — с текущего снимка
 * окна: прежде писались «Anonymous», «000000» и случайный UID исследования,
 * и снимок экрана не ложился в своё исследование. Снимки одного
 * исследования за сеанс — одна серия «Снимок экрана», номера по порядку.
 */
const _серииSC = new Map();   // UID исследования → {uid серии, номер следующего снимка}

function _метаСнимка(vp) {
  let кадр = null;
  try { кадр = vp.getCurrentImageId?.() || null; } catch (e) { кадр = null; }
  if (!кадр) { try { кадр = (vp.getImageIds?.() || [])[0] || null; } catch (e) { кадр = null; } }
  if (!кадр) return {};
  const m = (тип) => { try { return cornerstone.metaData.get(тип, кадр) || {}; } catch (e) { return {}; } };
  const пац = m('patientModule'), иссл = m('generalStudyModule'), пацИссл = m('patientStudyModule');
  const имя = (v) => (typeof v === 'string' ? v : v?.Alphabetic || '');
  return {
    пациент: { имя: имя(пац.patientName), id: пац.patientId || '', рождение: пац.patientBirthDate || пацИссл.patientBirthDate || '', пол: пац.patientSex || '' },
    исследование: { uid: иссл.studyInstanceUID || '', дата: иссл.studyDate || '', время: String(иссл.studyTime || '').split('.')[0], id: иссл.studyID || '', accession: иссл.accessionNumber || '', описание: иссл.studyDescription || '', направил: имя(иссл.referringPhysicianName) },
  };
}

export function exportDICOMSecondaryCapture(viewportId, meta = {}) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return null;
  const canvas = vp.canvas || vp.element?.querySelector('canvas');
  if (!canvas) return null;
  const w = canvas.width, h = canvas.height;
  const offscreen = document.createElement('canvas');
  offscreen.width = w; offscreen.height = h;
  const ctx = offscreen.getContext('2d');
  try { ctx.drawImage(canvas, 0, 0); } catch { return null; }
  const imgData = ctx.getImageData(0, 0, w, h);
  const rgb = new Uint8Array(w * h * 3);
  for (let i = 0, j = 0; i < imgData.data.length; i += 4, j += 3) {
    rgb[j] = imgData.data[i];
    rgb[j + 1] = imgData.data[i + 1];
    rgb[j + 2] = imgData.data[i + 2];
  }
  const снимок = _метаСнимка(vp);
  const пациент = { ...снимок.пациент, ...(meta.patientName != null ? { имя: meta.patientName } : {}), ...(meta.patientId != null ? { id: meta.patientId } : {}) };
  const исследование = { ...снимок.исследование, ...(meta.studyInstanceUID ? { uid: meta.studyInstanceUID } : {}) };
  const ключ = исследование.uid || '—';
  if (!_серииSC.has(ключ)) _серииSC.set(ключ, { uid: meta.seriesInstanceUID || `${ПРИСТАВКА_UID}.${Date.now()}.${Math.floor(Math.random() * 1e9)}`, следующий: 1 });
  const серия = _серииSC.get(ключ);
  const байты = собратьSC({
    ширина: w, высота: h, rgb, пациент, исследование,
    серия: { uid: серия.uid, номер: 999, описание: meta.seriesDescription || 'Снимок экрана' },
    экземпляр: { uid: meta.sopInstanceUID || undefined, номер: серия.следующий++ },
  });
  return new Blob([байты], { type: 'application/dicom' });
}

/**
 * [12.0.57] Возвращает true/false и говорит сам — один тост на нажатие
 * (кнопка добавляла свой «💾 DICOM SC сохранён» даже после «❌ Ошибка DICOM»).
 * Ссылка на файл отпускается не сразу: Safari и Firefox не успевают начать
 * скачивание, если отпустить её в том же такте.
 */
export function downloadDICOMCapture(viewportId, filename, meta = {}) {
  let blob = null;
  try { blob = exportDICOMSecondaryCapture(viewportId, meta); } catch (e) { log.warn('[DICOM SC]', e.message); }
  if (!blob) { showToast('DICOM SC не сохранён: окно не отдало картинку', 'error', 3000); return false; }
  const url = URL.createObjectURL(blob);
  _downloadDataUrl(url, filename || `SC_${Date.now()}.dcm`);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  showToast(`DICOM SC сохранён (${(blob.size / 1024).toFixed(0)} КБ) — снимок окна «${viewportId}» с данными исследования`, 'success', 2500);
  return true;
}

// [12.0.57] Помощники _dcmTag/_dcmUS/_dcmUL/_dcmOB/_dcmPixelData удалены: байты собирает
// core/scDicom.js (теги по возрастанию, UTF-8, чётные длины) — см. надгробие у exportDICOMSecondaryCapture.

// ══════════════════════════════════════════════════════════════
//  CLEANUP
// ══════════════════════════════════════════════════════════════

export function destroyScreenshot() {
  if (_screenRecorder) {
    try { _screenRecorder.stop(); } catch {}
    _screenRecorder = null;
  }
  _screenRecordingChunks = [];
}